import type { Entity, EntityId, WorldState } from "../../sim";
import { lerpAngle } from "../../sim/math/angle";
import { type WorldSize, wrappedDelta } from "../../sim/math/torus";

export interface Pose {
	x: number;
	y: number;
	angle: number;
}

/**
 * Remembers entity poses from before the latest sim step and blends towards the current
 * pose. Blending goes along the shortest wrapped path, so objects crossing the world seam
 * don't streak across the screen. Result coordinates may lie outside [0, size); the
 * renderer only uses them relative to a camera centre.
 */
export class Interpolator {
	private previous = new Map<EntityId, Pose>();

	/** Call right before each `step()`. */
	capture(world: WorldState): void {
		const seen = new Set<EntityId>();
		for (const e of world.entities) {
			if (e.kind === "planet" || e.kind === "spawnPoint") continue;
			seen.add(e.id);
			const pose = this.previous.get(e.id);
			if (pose) {
				pose.x = e.pos.x;
				pose.y = e.pos.y;
				pose.angle = e.angle;
			} else {
				this.previous.set(e.id, { x: e.pos.x, y: e.pos.y, angle: e.angle });
			}
		}
		for (const id of this.previous.keys()) if (!seen.has(id)) this.previous.delete(id);
	}

	/** Pose of `e` at `alpha` ∈ [0, 1] between the captured and the current state. */
	sample(e: Entity, alpha: number, size: WorldSize, out: Pose = { x: 0, y: 0, angle: 0 }): Pose {
		const prev = this.previous.get(e.id);
		if (!prev) {
			out.x = e.pos.x;
			out.y = e.pos.y;
			out.angle = e.angle;
			return out;
		}
		const d = wrappedDelta(prev, e.pos, size);
		out.x = prev.x + d.x * alpha;
		out.y = prev.y + d.y * alpha;
		out.angle = lerpAngle(prev.angle, e.angle, alpha);
		return out;
	}
}
